import { useState, useCallback } from 'react';

/**
 * Custom hook to manage quote data state
 * Handles quotes by product type, filtering, loading states, and errors
 */
const useQuoteData = () => {
  const [quotesByProductType, setQuotesByProductType] = useState({});
  const [originalQuotesByProductType, setOriginalQuotesByProductType] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [quotesError, setQuotesError] = useState(null);
  const [hasInitiatedQuoteRequest, setHasInitiatedQuoteRequest] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);

  /**
   * Update quotes for a specific product type
   */
  const updateQuotes = useCallback(
    (productType, quotes) => {
      setQuotesByProductType(prev => ({
        ...prev,
        [productType]: quotes
      }));

      // Also update original if not filtered
      if (!appliedFilters) {
        setOriginalQuotesByProductType(prev => ({
          ...prev,
          [productType]: quotes
        }));
      }
    },
    [appliedFilters]
  );

  /**
   * Update all quotes at once
   */
  const updateAllQuotes = useCallback(
    quotes => {
      setQuotesByProductType(quotes);
      if (!appliedFilters) {
        setOriginalQuotesByProductType(quotes);
      }
    },
    [appliedFilters]
  );

  /**
   * Update original quotes (unfiltered)
   */
  const updateOriginalQuotes = useCallback(quotes => {
    setOriginalQuotesByProductType(quotes);
  }, []);

  /**
   * Clear all quotes
   */
  const clearQuotes = useCallback(() => {
    setQuotesByProductType({});
    setOriginalQuotesByProductType({});
    setAppliedFilters(null);
  }, []);

  /**
   * Set loading state for a product type
   */
  const setLoadingState = useCallback((productType, isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      [productType]: isLoading
    }));
  }, []);

  /**
   * Clear all loading states
   */
  const clearLoadingStates = useCallback(() => {
    setLoadingStates({});
  }, []);

  /**
   * Check if any quotes are loading
   */
  const isAnyLoading = useCallback(() => {
    return Object.values(loadingStates).some(value => value === true);
  }, [loadingStates]);

  /**
   * Set error message
   */
  const setError = useCallback(error => {
    setQuotesError(error);
  }, []);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setQuotesError(null);
  }, []);

  /**
   * Mark that a quote request has been initiated
   */
  const markQuoteRequestInitiated = useCallback(() => {
    setHasInitiatedQuoteRequest(true);
  }, []);

  /**
   * Reset quote request flag
   */
  const resetQuoteRequestFlag = useCallback(() => {
    setHasInitiatedQuoteRequest(false);
  }, []);

  /**
   * Apply filters to quotes
   */
  const applyFilters = useCallback(filters => {
    setAppliedFilters(filters);
  }, []);

  /**
   * Clear applied filters and restore original quotes
   */
  const clearFilters = useCallback(() => {
    setAppliedFilters(null);
    setQuotesByProductType(originalQuotesByProductType);
  }, [originalQuotesByProductType]);

  /**
   * Get quotes for a specific product type
   */
  const getQuotesForProductType = useCallback(
    productType => {
      return quotesByProductType[productType] || [];
    },
    [quotesByProductType]
  );

  /**
   * Get all quotes as a flat array
   */
  const getAllQuotes = useCallback(() => {
    return Object.values(quotesByProductType).flat();
  }, [quotesByProductType]);

  /**
   * Get count of quotes for a product type
   */
  const getQuoteCount = useCallback(
    productType => {
      return (quotesByProductType[productType] || []).length;
    },
    [quotesByProductType]
  );

  /**
   * Get total count of all quotes
   */
  const getTotalQuoteCount = useCallback(() => {
    return getAllQuotes().length;
  }, [getAllQuotes]);

  /**
   * Check if quotes are available
   */
  const hasQuotes = useCallback(() => {
    return Object.keys(quotesByProductType).length > 0 && getTotalQuoteCount() > 0;
  }, [quotesByProductType, getTotalQuoteCount]);

  /**
   * Check if original quotes are available
   */
  const hasOriginalQuotes = useCallback(() => {
    return Object.keys(originalQuotesByProductType).length > 0;
  }, [originalQuotesByProductType]);

  /**
   * Get available product types from quotes
   */
  const getAvailableProductTypes = useCallback(() => {
    return Object.keys(quotesByProductType).filter(type => quotesByProductType[type] && quotesByProductType[type].length > 0);
  }, [quotesByProductType]);

  return {
    // State
    quotesByProductType,
    originalQuotesByProductType,
    loadingStates,
    quotesError,
    hasInitiatedQuoteRequest,
    appliedFilters,

    // Computed values
    isAnyLoading: isAnyLoading(),
    hasQuotes: hasQuotes(),
    hasOriginalQuotes: hasOriginalQuotes(),
    totalQuoteCount: getTotalQuoteCount(),

    // Getters
    getQuotesForProductType,
    getAllQuotes,
    getQuoteCount,
    getTotalQuoteCount,
    getAvailableProductTypes,

    // Actions
    updateQuotes,
    updateAllQuotes,
    updateOriginalQuotes,
    clearQuotes,
    setLoadingState,
    clearLoadingStates,
    setError,
    clearError,
    markQuoteRequestInitiated,
    resetQuoteRequestFlag,
    applyFilters,
    clearFilters,

    // State setters for direct access
    setQuotesByProductType,
    setOriginalQuotesByProductType,
    setLoadingStates,
    setQuotesError,
    setHasInitiatedQuoteRequest,
    setAppliedFilters
  };
};

export default useQuoteData;
